import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { blogPosts } from '../data/blogPosts';
import BlogCover from './BlogCover';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Seconds for one full loop of the cards; scales with the number of posts so
// the drift speed stays the same as posts are added.
const SECONDS_PER_CARD = 9;

function BlogCard({ post, colors, hidden = false }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/blog/${post.slug}`}
      // The second copy of the cards only exists to make the loop seamless;
      // keep it out of the tab order and away from screen readers.
      tabIndex={hidden ? -1 : undefined}
      aria-hidden={hidden || undefined}
      className="blog-card"
      style={{
        display: 'block', background: colors.surface, border: '1px solid',
        borderColor: hovered ? colors.accent : colors.border,
        transform: hovered ? 'translateY(-4px)' : 'none',
        transition: 'transform 0.3s, border-color 0.3s', textDecoration: 'none',
        overflow: 'hidden',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <BlogCover cover={post.cover} height="170px" sizes="340px" />
      <div style={{ padding: '1.4rem 1.5rem 1.6rem' }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem',
          color: colors.muted, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '0.6rem',
        }}>
          {post.category} · {formatDate(post.date)}
        </div>
        <h3 style={{
          fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: '1.15rem',
          letterSpacing: '0.3px', color: colors.text, marginBottom: '0.6rem', lineHeight: 1.25,
        }}>
          {post.title}
        </h3>
        <p style={{ fontSize: '0.85rem', color: colors.muted, lineHeight: 1.7, fontWeight: 300 }}>
          {post.excerpt}
        </p>
      </div>
    </Link>
  );
}

export default function Blog() {
  const { colors } = useTheme();
  const posts = [...blogPosts].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Only animate while the strip is on screen.
  const viewportRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const s = {
    section: { padding: '100px 0', background: colors.bg2 },
    header: {
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      textAlign: 'center', gap: '1.5rem', marginBottom: '3rem', padding: '0 5vw',
    },
    title: {
      fontFamily: "'Inter', sans-serif", fontWeight: 700,
      fontSize: 'clamp(2rem, 4vw, 3.5rem)',
      lineHeight: 1, letterSpacing: '2px', color: colors.heading,
    },
    viewAll: {
      border: `1px solid ${colors.text}`, color: colors.text,
      padding: '0.7rem 1.6rem', fontSize: '0.8rem', fontWeight: 600,
      letterSpacing: '1px', textTransform: 'uppercase', textDecoration: 'none',
      display: 'inline-block', whiteSpace: 'nowrap',
    },
    viewAllWrap: { display: 'flex', justifyContent: 'center', marginTop: '3rem' },
  };

  return (
    <section id="blog" style={s.section}>
      <div style={s.header} className="blog-header">
        <div>
          <h2 style={s.title}>LATEST INSIGHTS</h2>
        </div>
      </div>

      {/* Cards drift sideways in a seamless loop: the list is rendered twice
          and the track slides by exactly one copy's width. Pauses on hover or
          keyboard focus, and when scrolled out of view. */}
      <div ref={viewportRef} className="blog-marquee">
        <div
          className="blog-marquee-track"
          style={{
            animationDuration: `${posts.length * SECONDS_PER_CARD}s`,
            animationPlayState: inView ? undefined : 'paused',
          }}
        >
          {[false, true].map(isCopy => posts.map(post => (
            <BlogCard key={`${post.slug}${isCopy ? '-copy' : ''}`} post={post} colors={colors} hidden={isCopy} />
          )))}
        </div>
      </div>

      <div style={s.viewAllWrap}>
        <Link to="/blog" style={s.viewAll}>View All Posts →</Link>
      </div>

      <style>{`
        .blog-marquee {
          overflow: hidden;
          padding: 8px 0; /* room for the hover lift */
          /* soft fade at both edges */
          -webkit-mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);
                  mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);
        }
        .blog-marquee-track {
          display: flex;
          width: max-content;
          animation-name: blog-drift;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .blog-marquee:hover .blog-marquee-track,
        .blog-marquee:focus-within .blog-marquee-track {
          animation-play-state: paused;
        }
        .blog-card {
          flex: 0 0 340px;
          margin-right: 1.5rem; /* margin (not gap) keeps both copies exactly equal width */
        }
        @keyframes blog-drift {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        /* Visitors who prefer reduced motion get a still, swipeable row instead */
        @media (prefers-reduced-motion: reduce) {
          .blog-marquee { overflow-x: auto; -webkit-mask-image: none; mask-image: none; padding-left: 5vw; }
          .blog-marquee-track { animation: none; }
          .blog-card[aria-hidden="true"] { display: none; }
        }
        @media (max-width: 600px) {
          .blog-card { flex-basis: 280px; }
          .blog-header h2 { white-space: normal !important; }
        }
      `}</style>
    </section>
  );
}
